import "./verify.scss";
import { useWeb3Context } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import { IReduxState } from "src/store/slices/state.interface";
import { claim } from "src/store/slices/airdrop-thunk";
import { useState, useCallback, useEffect } from "react";
import { Grid, InputAdornment, OutlinedInput, Zoom, Box, Modal, withStyles, Tooltip } from "@material-ui/core";
import { formatValue, trim } from "../../helpers";
import { IPendingTxn, isPendingTxn, txnButtonText } from "../../store/slices/pending-txns-slice";
import { Skeleton } from "@material-ui/lab";
import { ethers } from "ethers";
import { getAddresses, Networks } from "src/constants";
import { AirdropContract, MasterMantisContract, MntVerifyContract, StableReserveContract } from "src/abi";
import { fetchAccountSuccess } from "src/store/slices/account-slice";
import { changeApproval, lock } from "src/store/slices/verify-thunk";
import InfoIcon from "../../assets/icons/info-circle.svg";

function MntVerify() {
    const dispatch = useDispatch();
    const { provider, address, connect, chainID, checkWrongNetwork } = useWeb3Context();
    const addresses = getAddresses(chainID);

    const [amount, setAmount] = useState<string>("");
    const [mntsAllowance, setMntsAllowance] = useState<number>(0);
    const [eMntsBalance, seteMntsBalance] = useState<string>("");

    const [claimable, setClaimable] = useState<string>("");

    const isAppLoading = useSelector<IReduxState, boolean>(state => state.app.loading);
    const mntsBalance = useSelector<IReduxState, string>(state => {
        return state.account.balances && state.account.balances.mnts;
    });
    const pendingTransactions = useSelector<IReduxState, IPendingTxn[]>(state => {
        return state.pendingTransactions;
    });

    const onLock = async () => {
        if (await checkWrongNetwork()) return;
        await dispatch(lock({ address, provider, networkID: chainID }));
    };

    const onSeekApproval = async (token: string) => {
        if (await checkWrongNetwork()) return;
        await dispatch(changeApproval({ address, token, provider, networkID: chainID }));
    };

    useEffect(() => {
        const getAmount = async () => {
            if (address) {
                const masterMantisContract = new ethers.Contract(addresses.MASTER_MANTIS_ADDRESS, MasterMantisContract, provider);
                const mntsContract = new ethers.Contract(addresses.MNTS_ADDRESS, StableReserveContract, provider);
                const eMntsContract = new ethers.Contract(addresses.EMNTS_ADDRESS, StableReserveContract, provider);
                const mnts_balance = await mntsContract.balanceOf(address);
                dispatch(
                    fetchAccountSuccess({
                        balances: {
                            mnts: ethers.utils.formatUnits(mnts_balance, "ether"),
                        },
                    }),
                );

                const mnts_allowance = await mntsContract.allowance(address, addresses.MNT_VERIFY_ADDRESS);
                setMntsAllowance(Number(mnts_allowance));
                const emnts_balance = await eMntsContract.balanceOf(address);
                seteMntsBalance(ethers.utils.formatUnits(emnts_balance, "ether"));
                let pendingMnts = 0;
                pendingMnts += Number(ethers.utils.formatUnits((await masterMantisContract.pendingMnt(0, address)).pendingMNT, "ether"));
                pendingMnts += Number(ethers.utils.formatUnits((await masterMantisContract.pendingMnt(1, address)).pendingMNT, "ether"));
                pendingMnts += Number(ethers.utils.formatUnits((await masterMantisContract.pendingMnt(2, address)).pendingMNT, "ether"));
                setClaimable(pendingMnts.toString());
                // const verifyContract = new ethers.Contract(addresses.MNT_VERIFY_ADDRESS, MntVerifyContract, provider);
                // const locked_amount = await verifyContract.mntStaked(address);
                // setAmount(ethers.utils.formatUnits(locked_amount, "ether"));
            }
        };
        getAmount();
    }, [pendingTransactions]);

    const LightTooltip = withStyles(theme => ({
        arrow: {
            color: "rgba(11, 0, 30, 1)",
        },
        tooltip: {
            backgroundColor: "rgba(11, 0, 30, 1)",
            fontSize: 11,
            fontStyle: "Proxima",
        },
    }))(Tooltip);

    return (
        <div className="verify-view">
            <Zoom in={true}>
                <div className="verify-card mb-3">
                    <Grid className="verify-card-grid" container direction="column" spacing={2}>
                        <Grid item>
                            <div className="verify-card-header">
                                <p className="verify-card-header-title">MNT Holders</p>
                            </div>
                            <div style={{ color: "white", marginTop: "30px" }}>
                                <p style={{ marginBottom: "6px" }}>- Confirm your MNT Holdings</p>
                                <p style={{ marginBottom: "6px" }}>- Lock MNT to receive eMNTS</p>
                                <p style={{ marginBottom: "6px" }}>- You will be airdropped MNTS in 1:1 ratio for eMNTS in your wallet</p>
                            </div>
                        </Grid>

                        <div className="verify-card-area">
                            {!address && (
                                <div className="verify-card-wallet-notification">
                                    <div className="verify-card-wallet-connect-btn" onClick={connect}>
                                        <p>Connect Wallet</p>
                                    </div>
                                    <p className="verify-card-wallet-desc-text">Connect your wallet!</p>
                                </div>
                            )}
                            {address && (
                                <div>
                                    {Number(claimable) > 0 && (
                                        <div className="verify-user-data">
                                            <div className="data-row">
                                                <p className="data-row-name">
                                                    Unclaimed MNT{" "}
                                                    <LightTooltip title="Claim your MNT on Pool page" placement="top-start" arrow>
                                                        <img className="tooltip-icon" alt="" src={InfoIcon} width={12} height={12} />
                                                    </LightTooltip>
                                                </p>
                                                <p className="data-row-value">{isAppLoading || !mntsBalance ? <Skeleton width="80px" /> : <>{formatValue(claimable)} MNT</>}</p>
                                            </div>
                                        </div>
                                    )}
                                    {Number(mntsBalance) > 0 && (
                                        <>
                                            <div className="verify-user-data">
                                                <div className="data-row-main">
                                                    <p className="data-row-name">MNT BALANCE</p>
                                                    <p className="data-row-value">
                                                        {isAppLoading || !mntsBalance ? <Skeleton width="80px" /> : <>{formatValue(mntsBalance)} MNT</>}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="verify-card-action-area">
                                                <div className="verify-card-action-row">
                                                    <div className="verify-card-tab-panel-full">
                                                        {mntsAllowance > 0 ? (
                                                            <div
                                                                className="verify-card-tab-panel-btn"
                                                                onClick={() => {
                                                                    if (isPendingTxn(pendingTransactions, "verify_stake")) return;
                                                                    onLock();
                                                                }}
                                                            >
                                                                <p>{txnButtonText(pendingTransactions, "verify_stake", "Lock MNT")}</p>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="verify-card-tab-panel-btn"
                                                                onClick={() => {
                                                                    if (isPendingTxn(pendingTransactions, "approve_" + "mnts")) return;
                                                                    onSeekApproval("mnts");
                                                                }}
                                                            >
                                                                <p>{txnButtonText(pendingTransactions, "approve_" + "mnts", "Approve")}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {Number(eMntsBalance) > 0 && (
                                        <div className="verify-user-data">
                                            <div className="data-row">
                                                <p className="data-row-name">eMNTS Balance</p>
                                                <p className="data-row-value">{isAppLoading ? <Skeleton width="80px" /> : <>{formatValue(eMntsBalance)} eMNTS</>}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Grid>
                </div>
            </Zoom>
        </div>
    );
}
export default MntVerify;
